import { useD3 } from '../../../../../components/utils/useD3'
import * as d3 from 'd3'
import { useMemo } from 'react'
import { ReportBlockPreview } from '../ReportBlockPreview'

import styles from './ReportBlockChart.module.scss'

/** @param {import('./ReportBlockBarChartPreview.types').ReportBlockBarChartPreviewProps} props */
export const ReportBlockBarChartPreview = ({ title, data, maxWidth = 300, showLegend = true }) => {
  const ref = useD3((svg) => {
    const height = 300
    const width = 450
    // Bigger margin for higher numbers
    const sideMargin = d3.max(data, (d) => d.count) || 0 > 999 ? 50 : 40
    const margin = {
      top: 20,
      right: sideMargin,
      bottom: 0,
      left: sideMargin
    }
    svg.attr('viewBox', '0 0 ' + width + ' ' + (height + margin.top * 2))

    const xScale = d3
      .scaleBand()
      .domain(data.map((d) => d.label))
      .rangeRound([margin.left, width])
      .padding(0.1)

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.count) || 0])
      .rangeRound([height - margin.bottom, margin.top])
      .nice()

    const yAxis = (g) =>
      g
        .attr('transform', `translate(${margin.left},0)`)
        .style('color', 'steelblue')
        .call(d3.axisLeft(yScale).ticks(null, 's'))
        .call((g) => g.select('.domain').remove())
        .call((g) => g.append('text').attr('x', -margin.left).attr('y', 10).attr('fill', 'currentColor').attr('text-anchor', 'start').text(data.y1))
        .call((g) =>
          g
            .selectAll('line')
            .attr('x1', width - margin.right)
            .attr('x2', 0)
            .attr('stroke', '#9BAFC3')
            .attr('stroke-width', '2')
        )
        .call((g) => g.selectAll('text').attr('dy', '0.4rem').attr('font-size', '1.2rem').attr('fill', 'black'))

    svg.select('.y-axis').call(yAxis)
    svg.select('.y-axis .tick:first-child').remove()

    const xAxis = (g) =>
      g.attr('transform', `translate(0,${height - margin.bottom})`).call(
        d3
          .axisBottom()
          .scale(xScale)
          .tickValues(d3.ticks(...d3.extent(xScale.domain()), width / 40).filter((v) => xScale(v) !== undefined))
          .tickSizeOuter(3)
      )

    svg.select('.x-axis').call(xAxis).selectAll('path').attr('fill', 'black')

    const total = d3.sum(data, (d) => d.count)

    svg
      .select('.plot-area')
      .attr('fill', 'lightblue')
      .selectAll('.bar')
      .data(data)
      .join('rect')
      .attr('class', 'bar')
      .attr('x', (d) => xScale(d.label))
      .attr('width', xScale.bandwidth())
      .attr('y', (d) => yScale(d.count))
      .attr('height', (d) => yScale(0) - yScale(d.count))
      .attr('fill', (d) => d.color || 'lightblue')
      .attr('style', `outline: 1px solid black;`)

    svg
      .select('.plot-area')
      .selectAll('.label')
      .data(data)
      .join('text')
      .attr('class', 'label')
      .attr('x', (d) => xScale(d.label) + xScale.bandwidth() / 2)
      .attr('y', (d) => {
        const barHeight = yScale(0) - yScale(d.count)
        const barPercentage = (1 / total) * d.count
        return barPercentage <= 0.1 ? yScale(d.count) - 10 : yScale(d.count) + barHeight / 2
      })
      .attr('text-anchor', 'middle')
      .text((d) => {
        return `${Math.round((100 / total) * d.count)}%`
      })
      .attr('fill', 'black')
      .style('font-size', '16px')
  }, data)

  return (
    <ReportBlockPreview className={styles.chart}>
      {!!title && <h2>{title}</h2>}
      {data.length === 0 ? (
        <p>There is not enough data</p>
      ) : (
        <div className={styles['chart-graphic']}>
          <svg ref={ref} style={{ maxWidth }}>
            <g className='y-axis' />
            <g className='plot-area' />
            <g className='x-axis' />
          </svg>
          {showLegend && data.length > 0 && <ChartLegend data={data} />}
        </div>
      )}
    </ReportBlockPreview>
  )
}

/**
 * @param {object} props
 * @param {import('./ReportBlockBarChartPreview.types').ChartDataItem[]} props.data
 */
const ChartLegend = ({ data }) => {
  const total = useMemo(() => {
    return data.reduce((p, { count }) => {
      p += count
      return p
    }, 0)
  }, [data])

  return (
    <div className={styles.legend}>
      <ul>
        {data.map(({ color, label, count }, index) => (
          <li key={index}>
            <span className={styles.circle} style={{ backgroundColor: color }}></span>
            {Math.floor((100 / total) * count)}% ({count}) {label}
          </li>
        ))}
      </ul>
    </div>
  )
}
